import Decimal from 'decimal.js';
import { cloneDeep, compact, keyBy, uniqueId } from 'lodash';

import {
  deriveSemanticTileKind,
  getOutOfEstateCategoryIcon,
} from '@/components/diagrams/components/Tile/Tile.utils';
import { TileVariant } from '@/components/diagrams/components/Tile/types';
import { Node, TileNode } from '@/components/diagrams/FlowChart';
import { TileNodeBeforeAndAfter } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNodeBeforeAndAfter';
import { isTileGroupNode } from '@/components/diagrams/FlowChart/utils/nodes';
import {
  AfterDeath,
  ClientOrganizationKind,
  EntityInEstateStatus,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { formatCurrency } from '@/utils/formatting/currency';

import { EstateWaterfallEdgeLabel } from '../components/EstateWaterfallEdgeLabel/estateWaterfallEdgeLabel.component';
import { EstateWaterfallEdgeLabelProps } from '../components/EstateWaterfallEdgeLabel/types';
import { EstateWaterfallTileEditModal } from '../components/EstateWaterfallTileEditModal';
import { getIsCharitableNode } from '../EstateWaterfall.utils';
import {
  EstateWaterfall_NodeFragment,
  EstateWaterfall_VizConfigFragment,
  EstateWaterfallFragment,
} from '../graphql/EstateWaterfall.generated';
import {
  EstateWaterfallGraph,
  EstateWaterfallGraphNodeAttributes,
  GraphNodeCategorizationType,
} from '../types';
import {
  categorizationTypeToVariant,
  EntityNode,
  ESTATE_TAX_NODE_IDENTIFIER,
  EXTERNAL_TRANSFER_NODE_IDENTIFIER,
  GIFT_TAX_NODE_IDENTIFIER,
  kindToCategorizationType,
  UI_ONLY_GROUP_NODE_IDENTIFIER,
  ValidTypeName,
  ValidTypeNamesSet,
} from './constants';
import { MIN_NODES_IN_GROUP } from './constants';
import { SHOW_CREATE_GROUP_MODAL_SENTINEL } from './CreateNewGroupModal.utils';
import { createEdge } from './waterfallGraph';

export function getNodeId({
  id,
  afterDeath,
}: Pick<EstateWaterfall_NodeFragment, 'id' | 'afterDeath'>) {
  return [afterDeath, id].join(':');
}

/**
 * Inverse of {@link getNodeId}: recovers the underlying entity/group id, i.e.
 * everything after the leading `{afterDeath}:` prefix. The id itself may
 * contain colons, so only the first colon is treated as the separator.
 */
export function getEntityIdFromNodeId(nodeId: string): string {
  return nodeId.slice(nodeId.indexOf(':') + 1);
}

export function isTaxNode(nodeId: string): boolean {
  return new RegExp(ESTATE_TAX_NODE_IDENTIFIER).test(nodeId);
}

export function isGiftTaxNode(nodeId: string): boolean {
  return new RegExp(GIFT_TAX_NODE_IDENTIFIER).test(nodeId);
}

export function isUIOnlyGroupNode(nodeId: string): boolean {
  return new RegExp(UI_ONLY_GROUP_NODE_IDENTIFIER).test(nodeId);
}

export function isEntityNode(
  n: EstateWaterfall_NodeFragment['node']
): n is EntityNode {
  return ValidTypeNamesSet.has(n.__typename as ValidTypeName);
}

/** The display name for a waterfall node, by underlying record type. */
export function getWaterfallNodeDisplayName(node: EntityNode): string {
  switch (node.__typename) {
    case 'Entity':
      return node.subtype.displayName;
    case 'TestamentaryEntity':
    case 'ClientProfile':
    case 'Proposal':
      return node.displayName;
    case 'ClientOrganization':
      return node.name;
    default:
      return '';
  }
}

export function getTaxNodeId({
  afterDeath,
}: Pick<EstateWaterfall_NodeFragment, 'afterDeath'>) {
  return getNodeId({ id: ESTATE_TAX_NODE_IDENTIFIER, afterDeath });
}

export function getGiftTaxNodeId({
  afterDeath,
}: Pick<EstateWaterfall_NodeFragment, 'afterDeath'>) {
  return getNodeId({ id: GIFT_TAX_NODE_IDENTIFIER, afterDeath });
}

export function isNodeIdForSection(nodeId: string, sectionId: AfterDeath) {
  return nodeId.startsWith(`${sectionId}:`);
}

export function getExternalTransferNodeId(
  transferKind: 'incoming' | 'outgoing'
) {
  return getNodeId({
    id: `${transferKind}-${EXTERNAL_TRANSFER_NODE_IDENTIFIER}`,
    afterDeath:
      transferKind === 'incoming' ? AfterDeath.None : AfterDeath.First,
  });
}

export function getEstateTaxAfterDeath(afterDeath: AfterDeath): AfterDeath {
  if (afterDeath === AfterDeath.None) return AfterDeath.First;
  if (afterDeath === AfterDeath.First) return AfterDeath.Second;
  // Context: https://withluminary.slack.com/archives/C05BPQS8CG4/p1692220364855759
  throw new Error(
    `Cannot traverse estate tax after death target past: ${afterDeath}`
  );
}

export function getNodeGroupId(
  node: EstateWaterfallGraphNodeAttributes
): string | null {
  return 'group' in node.data ? (node.data.group?.id ?? null) : null;
}

export function getCategorizationType({
  node,
}: EstateWaterfall_NodeFragment): GraphNodeCategorizationType | null {
  let kind;
  if (node.__typename === 'Entity') {
    kind = node.entityKind;
  } else if (node.__typename === 'TestamentaryEntity') {
    kind = node.testamentaryEntityKind;
  } else if (
    node.__typename === 'ClientProfile' ||
    node.__typename === 'ClientOrganization'
  ) {
    return GraphNodeCategorizationType.Individual;
  } else if (node.__typename === 'Proposal') {
    return GraphNodeCategorizationType.CharitableEntity;
  } else {
    throw new Error(`Unhandled tile in estate waterfall`);
  }

  const type = kindToCategorizationType[kind];
  if (!type) {
    diagnostics.error(`Unhandled type from kind in estate waterfall ${type}`);
    return null;
  }
  return type;
}

export interface BuildTileFromNodeInput {
  nodeFragment: EstateWaterfall_NodeFragment;
  visualizationConfig?: EstateWaterfall_VizConfigFragment | null;
  isNewTile?: boolean;
  hasHypotheticalTransfer?: boolean;
  hasIncomingPouroverDisposition?: boolean;
  hasOutgoingPouroverDisposition?: boolean;

  baseNodes?: EstateWaterfall_NodeFragment[];
  isHypotheticalWaterfall: boolean;
}

export interface GetBeforeAndAfterDataOptions {
  hasIncomingPouroverDisposition: boolean | undefined;
  hasOutgoingPouroverDisposition: boolean | undefined;
  hasHypotheticalTransfer?: boolean | undefined;
}

interface GetBeforeAndAfterDataOutputBase {
  beforeActionsValue: Decimal | null;
  afterActionsValue: Decimal | null;
  shouldShowBeforeAndAfter: boolean;
}

interface GetBeforeAndAfterShowData extends GetBeforeAndAfterDataOutputBase {
  beforeActionsValue: Decimal;
  afterActionsValue: Decimal;
  shouldShowBeforeAndAfter: true;
}

interface GetBeforeAndAfterHideData extends GetBeforeAndAfterDataOutputBase {
  shouldShowBeforeAndAfter: false;
}

export type GetBeforeAndAfterDataOutput =
  | GetBeforeAndAfterShowData
  | GetBeforeAndAfterHideData;

export function getBeforeAndAfterData(
  nodeFragment: BuildTileFromNodeInput['nodeFragment'],
  opts: GetBeforeAndAfterDataOptions,
  correspondingBaseNode: EstateWaterfall_NodeFragment | null,
  isHypotheticalWaterfall: boolean,
  hasHypotheticalTransfer?: boolean
): GetBeforeAndAfterDataOutput {
  const { hasIncomingPouroverDisposition, hasOutgoingPouroverDisposition } =
    opts;
  const {
    value,
    valueBeforeDispositions,
    valueBeforePouroverWills,
    afterDeath,
  } = nodeFragment;

  let calculatedBeforeValue: Decimal | null = null;
  let calculatedAfterValue: Decimal | null = value;

  // Pourover Wills
  // Determine the relevant 'before' and 'after' values for pourover calculations based on death timing.
  const { beforeValuePouroverWills, afterValuePouroverWills } = (() => {
    if (afterDeath === AfterDeath.None) {
      if (
        isHypotheticalWaterfall &&
        hasHypotheticalTransfer &&
        correspondingBaseNode &&
        hasIncomingPouroverDisposition
      ) {
        const netHypotheticalDifference =
          correspondingBaseNode.valueBeforePouroverWills?.minus(
            valueBeforePouroverWills ?? new Decimal(0)
          ) ?? new Decimal(0);

        return {
          beforeValuePouroverWills:
            valueBeforeDispositions?.plus(netHypotheticalDifference) ??
            new Decimal(0),
          afterValuePouroverWills: valueBeforeDispositions,
        };
      }

      if (
        isHypotheticalWaterfall &&
        hasHypotheticalTransfer &&
        correspondingBaseNode &&
        hasOutgoingPouroverDisposition
      ) {
        return {
          beforeValuePouroverWills:
            correspondingBaseNode.valueBeforePouroverWills ?? new Decimal(0),
          afterValuePouroverWills: valueBeforeDispositions,
        };
      }

      return {
        beforeValuePouroverWills: valueBeforePouroverWills,
        afterValuePouroverWills: valueBeforeDispositions,
      };
    } else if (afterDeath === AfterDeath.First) {
      // If it's the first death, the 'before' value is the current node value before pourover.
      return {
        beforeValuePouroverWills: value,
        afterValuePouroverWills: valueBeforeDispositions,
      };
    }
    // No relevant pourover values in other cases (e.g., second death).
    return { beforeValuePouroverWills: null, afterValuePouroverWills: null };
  })();

  // Show if relevant pourover values exist, differ, and a pourover disposition occurred.
  const shouldShowBeforeAndAfterPouroverWills =
    !!beforeValuePouroverWills &&
    !!afterValuePouroverWills &&
    (!!hasIncomingPouroverDisposition || !!hasOutgoingPouroverDisposition) &&
    !beforeValuePouroverWills.eq(afterValuePouroverWills);

  if (shouldShowBeforeAndAfterPouroverWills) {
    calculatedBeforeValue = beforeValuePouroverWills;
    calculatedAfterValue = afterValuePouroverWills;

    return {
      beforeActionsValue: calculatedBeforeValue,
      afterActionsValue: calculatedAfterValue,
      shouldShowBeforeAndAfter: true,
    };
  }

  // If there is a corresponding node in the base waterfall, use it for the before value
  if (correspondingBaseNode) {
    calculatedBeforeValue = correspondingBaseNode.value;
  }

  if (
    isHypotheticalWaterfall &&
    !calculatedBeforeValue?.eq(calculatedAfterValue)
  ) {
    return {
      beforeActionsValue: calculatedBeforeValue ?? new Decimal(0),
      afterActionsValue: calculatedAfterValue ?? new Decimal(0),
      shouldShowBeforeAndAfter: true,
    };
  }

  return {
    beforeActionsValue: calculatedBeforeValue ?? new Decimal(0),
    afterActionsValue: calculatedAfterValue ?? new Decimal(0),
    shouldShowBeforeAndAfter: false,
  };
}

export function buildTileFromNode({
  nodeFragment,
  visualizationConfig,
  isNewTile,
  hasHypotheticalTransfer,
  hasIncomingPouroverDisposition,
  hasOutgoingPouroverDisposition,
  baseNodes,
  isHypotheticalWaterfall,
}: BuildTileFromNodeInput): TileNode | null {
  const {
    id,
    nodeConfiguration,
    node,
    afterDeath,
    value,
    inEstateStatus,
    isHypothetical,
  } = nodeFragment;
  if (!isEntityNode(node)) {
    diagnostics.error(`Unhandled tile in estate waterfall`);
    return null;
  }

  const categorizationType = getCategorizationType(nodeFragment);
  if (!categorizationType) {
    // just return here; this is logged in getCategorizationType
    return null;
  }

  const lineOne = getWaterfallNodeDisplayName(node);
  let variant = categorizationTypeToVariant[categorizationType];

  if (inEstateStatus === EntityInEstateStatus.InEstate) {
    variant = TileVariant.Primary;
  }

  if (
    node.__typename === 'ClientOrganization' &&
    node.clientOrganizationKind ===
      ClientOrganizationKind.CharitableOrganization &&
    inEstateStatus !== EntityInEstateStatus.InEstate
  ) {
    variant = TileVariant.Tertiary;
  }

  const nodeId = getNodeId({ id, afterDeath });
  const categoryIcon = getOutOfEstateCategoryIcon(variant);
  let footerLabel: string | undefined = undefined;
  if (isHypothetical) {
    footerLabel = 'Hypothetical trust';
  } else if (hasHypotheticalTransfer) {
    footerLabel = 'Includes transfers';
  }

  const correspondingBaseNode =
    baseNodes?.find(
      (n) => getNodeId({ id: n.id, afterDeath: n.afterDeath }) === nodeId
    ) ?? null;

  /** Value before hypothetical transfers and/or pour-over dispositions are applied */
  const { beforeActionsValue, afterActionsValue, shouldShowBeforeAndAfter } =
    getBeforeAndAfterData(
      nodeFragment,
      {
        hasIncomingPouroverDisposition,
        hasOutgoingPouroverDisposition,
        hasHypotheticalTransfer,
      },
      correspondingBaseNode,
      isHypotheticalWaterfall,
      hasHypotheticalTransfer
    );

  let lineTwo;
  let lineThree;
  if (visualizationConfig?.hideEntityValues) {
    lineTwo = undefined;
  } else if (shouldShowBeforeAndAfter) {
    lineTwo = (
      <>
        {shouldShowBeforeAndAfter ? (
          <TileNodeBeforeAndAfter
            before={formatCurrency(beforeActionsValue, {
              notation: 'compact',
            })}
            after={formatCurrency(afterActionsValue, {
              notation: 'compact',
            })}
          />
        ) : null}
      </>
    );
  } else {
    lineTwo = formatCurrency(value, {
      notation: 'compact',
      currencySign: 'accounting',
    });
  }

  const futureDeathBenefitAmount =
    nodeFragment.futureDeathBenefit ?? new Decimal(0);

  if (
    futureDeathBenefitAmount.gt(0) &&
    !visualizationConfig?.hideEntityValues
  ) {
    lineThree = `Death benefit: ${formatCurrency(futureDeathBenefitAmount, { notation: 'compact' })}`;
  }

  return {
    id: nodeId,
    type: 'tile',
    position: {
      x: nodeConfiguration?.xPosition ?? 0,
      y: nodeConfiguration?.yPosition ?? 0,
    },
    data: {
      lineOne,
      lineTwo,
      lineThree,
      variant,
      footerLabel,
      sectionLabelId: afterDeath,
      isNewTile,
      height: undefined,
      editModal: ({ isOpen, onClose }) => (
        <EstateWaterfallTileEditModal isOpen={isOpen} onClose={onClose} />
      ),
      kind: deriveSemanticTileKind({
        __typename: node.__typename,
        isCharitableOrganization:
          node.__typename === 'ClientOrganization' &&
          node.clientOrganizationKind ===
            ClientOrganizationKind.CharitableOrganization,
        entityKind: node.__typename === 'Entity' ? node.entityKind : undefined,
      }),
      ...(categoryIcon && { slots: { Icon: categoryIcon } }),
    },
  };
}

function nodeIdToDataId(graph: EstateWaterfallGraph, id: string): string {
  return graph.getNodeAttributes(id).data.id;
}

export function mutateWaterfallWithFlowNodesPositions(
  waterfall: EstateWaterfallFragment,
  reactFlowNodes: Node[],
  waterfallNodeConfigurations: EstateWaterfallFragment['nodeConfigurations']
) {
  // Sync current node positions on the flow chart
  const reactFlowNodesById = keyBy(reactFlowNodes, (n) => n.id);
  const updatedNodeConfigurations = waterfallNodeConfigurations.map(
    (nodeConfig) => {
      const existingNode = reactFlowNodesById[nodeConfig.nodeID];
      if (!existingNode) return nodeConfig;
      return {
        ...nodeConfig,
        xPosition: existingNode.position.x,
        yPosition: existingNode.position.y,
      };
    }
  );
  waterfall.nodeConfigurations = updatedNodeConfigurations;
}

function cloneWaterfallAppendGroupNodes(
  graph: EstateWaterfallGraph,
  groupId: string,
  appendNodes: Node[],
  reactFlowNodes: Node[],
  waterfallNodeConfigurations: EstateWaterfallFragment['nodeConfigurations']
): EstateWaterfallFragment {
  const waterfall = cloneDeep(graph.getAttribute('waterfall'));

  const nodesToBeGrouped = appendNodes.map((n) => n.id);
  const nodesToBeGroupedSet = new Set(nodesToBeGrouped);

  // Track nodes being moved from other groups: sourceGroupId -> Set<dataId>
  const movedFromGroups = new Map<string, Set<string>>();

  // Mutate the existing nodes to have a parent of the new group
  waterfall.visualizationWithProjections.nodes =
    waterfall.visualizationWithProjections.nodes.map((vizNode) => {
      const nodeId = getNodeId({
        id: vizNode.id,
        afterDeath: vizNode.afterDeath,
      });

      if (!nodesToBeGroupedSet.has(nodeId)) return vizNode;

      // Track if this node is being moved from another group
      if (vizNode.group?.id && vizNode.group.id !== groupId) {
        if (!movedFromGroups.has(vizNode.group.id)) {
          movedFromGroups.set(vizNode.group.id, new Set());
        }
        movedFromGroups.get(vizNode.group.id)!.add(vizNode.id);
      }

      return { ...vizNode, group: { id: groupId } };
    });

  // Mutate the existing edges to map from their previous individual
  // relationships to the new group relationship
  waterfall.visualizationWithProjections.edges =
    waterfall.visualizationWithProjections.edges.map((vizEdge) => {
      return {
        ...vizEdge,
        from: nodesToBeGroupedSet.has(getNodeId(vizEdge.from))
          ? { ...vizEdge.from, group: { id: groupId } }
          : vizEdge.from,
        to: nodesToBeGroupedSet.has(getNodeId(vizEdge.to))
          ? { ...vizEdge.to, group: { id: groupId } }
          : vizEdge.to,
      };
    });

  // Find the matching group and update it to include the appended nodes
  waterfall.visualizationGroups.edges =
    waterfall.visualizationGroups.edges?.map((e) => {
      if (e?.node?.id !== groupId) return e;

      const nodesToAppend = nodesToBeGrouped.map((id) => ({
        id: nodeIdToDataId(graph, id),
      }));

      return {
        ...e,
        node: { ...e.node, nodes: [...e.node.nodes, ...nodesToAppend] },
      };
    });

  // Clean up source groups that lost nodes to the target group
  if (movedFromGroups.size > 0) {
    const groupsToDissolve = new Set<string>();
    // Capture each source group's namespace before we filter dissolved groups out
    const groupNamespaces = new Map<string, AfterDeath>();

    // Remove moved nodes from source group definitions and identify groups to dissolve
    waterfall.visualizationGroups.edges = waterfall.visualizationGroups.edges
      ?.map((e) => {
        if (!e?.node || e.node.id === groupId) return e;

        const movedNodeIds = movedFromGroups.get(e.node.id);
        if (!movedNodeIds) return e;

        // Groups always have a namespace; the nullable type is from the GraphQL schema
        groupNamespaces.set(e.node.id, e.node.namespace as AfterDeath);

        const remainingNodes = e.node.nodes.filter(
          (n) => !movedNodeIds.has(n.id)
        );
        if (remainingNodes.length < MIN_NODES_IN_GROUP) {
          groupsToDissolve.add(e.node.id);
        }

        return {
          ...e,
          node: { ...e.node, nodes: remainingNodes },
        };
      })
      // Remove dissolved groups
      ?.filter((e) => !e?.node || !groupsToDissolve.has(e.node.id));

    // Clear group refs for remaining nodes in dissolved groups
    if (groupsToDissolve.size > 0) {
      waterfall.visualizationWithProjections.nodes =
        waterfall.visualizationWithProjections.nodes.map((vizNode) => {
          if (vizNode.group && groupsToDissolve.has(vizNode.group.id)) {
            return { ...vizNode, group: null };
          }
          return vizNode;
        });

      // Clear edge group refs for dissolved group members
      waterfall.visualizationWithProjections.edges =
        waterfall.visualizationWithProjections.edges.map((vizEdge) => {
          const fromDissolved =
            vizEdge.from.group && groupsToDissolve.has(vizEdge.from.group.id);
          const toDissolved =
            vizEdge.to.group && groupsToDissolve.has(vizEdge.to.group.id);

          if (!fromDissolved && !toDissolved) return vizEdge;

          return {
            ...vizEdge,
            from: fromDissolved
              ? { ...vizEdge.from, group: null }
              : vizEdge.from,
            to: toDissolved ? { ...vizEdge.to, group: null } : vizEdge.to,
          };
        });

      // Remove node configurations for dissolved group nodes
      const dissolvedGroupNodeIds = new Set(
        Array.from(groupsToDissolve).map((gId) => {
          const namespace = groupNamespaces.get(gId)!;
          return getNodeId({ id: gId, afterDeath: namespace });
        })
      );
      waterfall.nodeConfigurations = waterfall.nodeConfigurations.filter(
        (cfg) => !dissolvedGroupNodeIds.has(cfg.nodeID)
      );
    }
  }

  // Sync current node positions on the flow chart
  mutateWaterfallWithFlowNodesPositions(
    waterfall,
    reactFlowNodes,
    waterfallNodeConfigurations
  );

  return waterfall;
}

export interface CloneWaterfallWithUpsertGroupProps {
  graph: EstateWaterfallGraph;
  groupNode: Node;
  appendNodes: Node[];
  reactFlowNodes: Node[];
  waterfallNodeConfigurations: EstateWaterfallFragment['nodeConfigurations'];
}

/**
 * @description Creates group nodes when they don't yet exist as a UI only group,
 * or adds new nodes to an existing group
 */
export function cloneWaterfallWithUpsertGroup({
  graph,
  groupNode,
  appendNodes,
  reactFlowNodes,
  waterfallNodeConfigurations,
}: CloneWaterfallWithUpsertGroupProps): EstateWaterfallFragment {
  // Simply append if it's already a grouped node
  if (isTileGroupNode(groupNode)) {
    const groupId = graph.getNodeAttributes(groupNode.id).data.id;
    return cloneWaterfallAppendGroupNodes(
      graph,
      groupId,
      appendNodes,
      reactFlowNodes,
      waterfallNodeConfigurations
    );
  }

  // No group exists yet, so include the drop target itself in list of nodes we want to group together
  const nodes = [groupNode, ...appendNodes];
  const afterDeath = graph.getNodeAttributes(groupNode.id).data.afterDeath;

  // Unique id is required for multiple UI only groups being created
  const groupId = uniqueId(UI_ONLY_GROUP_NODE_IDENTIFIER);
  const nodeId = getNodeId({ id: groupId, afterDeath });

  // Create a waterfall and add existing nodes to the new group
  const waterfall = cloneWaterfallAppendGroupNodes(
    graph,
    groupId,
    nodes,
    reactFlowNodes,
    waterfallNodeConfigurations
  );

  // Create a UI only group node with all nodes associated
  waterfall.visualizationGroups.edges = [
    ...(waterfall.visualizationGroups.edges || []),
    {
      node: {
        id: groupId,
        displayName: SHOW_CREATE_GROUP_MODAL_SENTINEL,
        description: '',
        namespace: afterDeath,
        nodes: nodes.map((node) => ({ id: nodeIdToDataId(graph, node.id) })),
      },
    },
  ];

  // Set the position of the group to the position of the first node that was grouped
  waterfall.nodeConfigurations = [
    ...waterfall.nodeConfigurations,
    {
      nodeID: nodeId,
      xPosition: groupNode.position.x,
      yPosition: groupNode.position.y,
    },
  ];

  return waterfall;
}

interface AddTaxEdgeInput {
  graph: EstateWaterfallGraph;
  source: string;
  target: string;
  value: Decimal;
}

export function getFeatureFlaggedEdgeLabel({
  source,
  target,
  edge,
  ...edgeLabelProps
}: EstateWaterfallEdgeLabelProps) {
  return (
    <EstateWaterfallEdgeLabel
      source={source}
      target={target}
      edge={edge}
      {...edgeLabelProps}
    />
  );
}

export function addTaxEdge({ graph, source, target, value }: AddTaxEdgeInput) {
  const giftTaxEdge = createEdge({
    source,
    target,
    data: {
      variant: 'destructive',
      edgeLabel: getFeatureFlaggedEdgeLabel({
        source,
        target,
        variant: 'destructive',
        label: 'Tax',
        value: formatCurrency(value, {
          notation: 'compact',
        }),
      }),
    },
  });

  graph.addEdgeSafe(source, target, { type: 'tax', edge: giftTaxEdge });
}

export function extractReactFlowNodesAndEdges(graph: EstateWaterfallGraph) {
  const nodes = compact(
    graph.mapNodes((_nodeId, node) => {
      // We don't want to surface nodes that belong to a group as individual nodes in the waterfall,
      // but we do want them to be available on the waterfallGraph for processing relationships
      return getNodeGroupId(node) ? null : node.node;
    })
  );

  const edges = graph.getEdges();

  return { nodes, edges };
}

export function getGroupNodeTileVariant(nodes: EstateWaterfall_NodeFragment[]) {
  if (
    // All nodes are out of estate, the variant is warning (navy)
    nodes.every((node) => node.inEstateStatus === EntityInEstateStatus.InEstate)
  ) {
    return TileVariant.Primary;
  }

  if (
    // All nodes are charitable and out of estate, variant is tertiary (blue)
    nodes.every(
      (vizNode) =>
        vizNode.inEstateStatus === EntityInEstateStatus.OutOfEstate &&
        getIsCharitableNode(vizNode.node)
    )
  ) {
    return TileVariant.Tertiary;
  }

  if (
    // All nodes are family giving (not charitable) and out of estate, variant is secondary (teal)
    nodes.every(
      (node) =>
        node.inEstateStatus === EntityInEstateStatus.OutOfEstate &&
        !getIsCharitableNode(node.node)
    )
  ) {
    return TileVariant.Secondary;
  }

  // Default to group variant (gray)
  return TileVariant.Default;
}
